import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import './VisitorCounter.css';

const STORAGE_KEY = 'civilfox_visitor_id';
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function createVisitorId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();

  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function getVisitorId() {
  try {
    const savedId = window.localStorage.getItem(STORAGE_KEY);
    if (savedId && UUID_PATTERN.test(savedId)) return savedId;

    const visitorId = createVisitorId();
    window.localStorage.setItem(STORAGE_KEY, visitorId);
    return visitorId;
  } catch (error) {
    console.error('Could not persist the visitor ID in local storage.', error);
    return createVisitorId();
  }
}

function getTotal(data) {
  if (!data?.success || !Number.isSafeInteger(data.total) || data.total < 0) {
    throw new Error('The visitor-count API returned an invalid response.');
  }
  return data.total;
}

export default function VisitorCounter() {
  const [total, setTotal] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let active = true;

    const updateCount = async () => {
      try {
        const response = await api.get('/public/visitor-count');
        const currentTotal = getTotal(response.data);
        if (active) {
          setTotal(currentTotal);
          setStatus('ready');
        }
      } catch (error) {
        console.error('Could not load the visitor count.', error);
        if (active) setStatus('error');
      }
    };

    const registerVisitor = async () => {
      try {
        const response = await api.post('/public/visitors', { visitorId: getVisitorId() });
        const currentTotal = getTotal(response.data);
        if (active) {
          setTotal(currentTotal);
          setStatus('ready');
        }
      } catch (error) {
        console.error('Could not register this visitor.', error);
        await updateCount();
      }
    };

    registerVisitor();
    const intervalId = window.setInterval(updateCount, 60_000);
    window.addEventListener('focus', updateCount);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', updateCount);
    };
  }, []);

  const displayTotal = total === null
    ? (status === 'error' ? 'Unavailable' : '…')
    : total.toLocaleString();

  return (
    <aside className="visitor-counter" aria-label="CivilFox visitor count">
      <div className="visitor-counter__content">
        <p className="visitor-counter__heading">People visiting CivilFox</p>
        <div className="visitor-counter__metric">
          <strong aria-live="polite">{displayTotal}</strong>
          <span>Total visitors</span>
        </div>
        <p className="visitor-counter__note">All-time unique browsers</p>
      </div>
    </aside>
  );
}
